/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import {
  IonButton,
  IonIcon,
  IonList,
} from '@ionic/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CrossIcon from '@/assets/icons/close.svg';
import DoubleLeft from '@/assets/icons/double-caret-left.svg';
import DoubleRight from '@/assets/icons/double-caret-right.svg';
import ArrowDown from '@/assets/icons/keyboard-arrow-down.svg';
import ArrowUp from '@/assets/icons/keyboard-arrow-up.svg';
import { DashboardColumns } from '@/interfaces/DashboardInterface/DashboardInterface';
import { getSelectedColumns, setDashboardColumns } from '@/redux/dashboard/dashboard';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import { resolveDashboardName } from '@/utils/dashboardColumnts';

import styles from './DashboardCustomizationModal.module.scss';

const ALL_COLUMNS: DashboardColumns[] = Object.values(DashboardColumns).filter(
  (column) => column !== DashboardColumns.STATUS,
);

export interface DashboardCustomizationModalProps {
  onDismiss: () => void;
}

export default function DashboardCustomizationModal({
  onDismiss,
}: DashboardCustomizationModalProps) {
  const dispatch = useDispatch();
  const storedSelectedColumns = useSelector(
    (state: RootState) => getSelectedColumns(state),
  ) as DashboardColumns[];
  const [selectedColumns, setSelectedColumns] = useState(storedSelectedColumns);
  const [availableColumns, setAvailableColumns] = useState<DashboardColumns[]>([]);
  const [currentAvailableColumn, setCurrentAvailableColumn] = useState<number | null>(null);
  const [currentSelectedColumn, setCurrentSelectedColumn] = useState<number | null>(null);

  useEffect(() => {
    const notSelectedColumns = ALL_COLUMNS.filter(
      (column) => !storedSelectedColumns.includes(column),
    );
    setAvailableColumns(notSelectedColumns);
    if (storedSelectedColumns.includes(DashboardColumns.AG_CODE)) {
      storageService.setAGCode(true);
    } else {
      storageService.setAGCode(false);
    }
  }, [storedSelectedColumns]);

  const transferItem = () => {
    if (currentAvailableColumn !== null) {
      const item = availableColumns[currentAvailableColumn];
      setAvailableColumns(availableColumns.filter((_, i) => i !== currentAvailableColumn));
      setSelectedColumns([...selectedColumns, item]);
      setCurrentAvailableColumn(null);
    } else if (currentSelectedColumn !== null) {
      const item = selectedColumns[currentSelectedColumn];
      setSelectedColumns(selectedColumns.filter((_, i) => i !== currentSelectedColumn));
      setAvailableColumns([...availableColumns, item]);
      setCurrentSelectedColumn(null);
    }
  };

  const handleContinue = () => {
    dispatch(setDashboardColumns([DashboardColumns.STATUS, ...selectedColumns]));
    onDismiss();
  };

  const moveColumn = (direction: 'up' | 'down') => {
    if (currentSelectedColumn === null) return;

    const newColumns = [...selectedColumns];
    const currentIndex = currentSelectedColumn;
    let newIndex: number;

    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < selectedColumns.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    [newColumns[currentIndex], newColumns[newIndex]] = [newColumns[newIndex],
      newColumns[currentIndex]];

    setSelectedColumns(newColumns);
    setCurrentSelectedColumn(newIndex);
  };

  const canMoveLeft = () => {
    if (currentAvailableColumn === null) return false;
    return availableColumns.includes(availableColumns[currentAvailableColumn]);
  };

  const canMoveRight = () => {
    if (currentSelectedColumn === null) return false;
    return selectedColumns.includes(selectedColumns[currentSelectedColumn]);
  };

  return (
    <div id="bottom-modal-dashboard-customization" className={styles.container}>
      <div className={styles.header}>
        <h3>Personalizar dashboard</h3>
        <IonIcon
          className={styles.closeIcon}
          src={CrossIcon}
          onClick={() => onDismiss()}
        />
      </div>

      <p className={styles.subtitle}>
        Selecciona los campos y el orden en el que quieres que se muestren tus operaciones
      </p>

      <div className={styles.listsWrapper}>

        <div className={styles.listsContainer}>
          <IonList id="available-columns" className={styles.columnList}>
            {availableColumns.map((column, index) => (
              <div
                key={`available-${index}`}
                className={`${styles.listItem} ${currentAvailableColumn === index ? styles.selected : ''}`}
                onClick={() => {
                  setCurrentAvailableColumn(index);
                  setCurrentSelectedColumn(null);
                }}
              >
                {resolveDashboardName(column, true)}
              </div>
            ))}
          </IonList>

          <div className={styles.transferButtonContainer}>
            <IonButton
              className="primary-button"
              onClick={transferItem}
              disabled={canMoveRight()}
            >
              <IonIcon src={DoubleRight} />
            </IonButton>
            <IonButton
              className="primary-button"
              onClick={transferItem}
              disabled={canMoveLeft()}
            >
              <IonIcon src={DoubleLeft} />
            </IonButton>
          </div>

          <IonList id="selected-columns" className={styles.columnList}>
            {selectedColumns.map((column, index) => (
              <div
                key={`selected-${column}`}
                className={`${styles.listItem} ${currentSelectedColumn === index ? styles.selected : ''}`}
                onClick={() => {
                  setCurrentSelectedColumn(index);
                  setCurrentAvailableColumn(null);
                }}
              >
                {resolveDashboardName(column, true)}
              </div>
            ))}
          </IonList>

          <div className={styles.buttonsContainer}>
            <IonButton
              className="secondary-button"
              onClick={() => moveColumn('up')}
              disabled={currentSelectedColumn === null || currentSelectedColumn === 0}
            >
              <IonIcon src={ArrowUp} />
            </IonButton>
            <IonButton
              className="secondary-button"
              onClick={() => moveColumn('down')}
              disabled={
                currentSelectedColumn === null
                || currentSelectedColumn === selectedColumns.length - 1
              }
            >
              <IonIcon src={ArrowDown} />
            </IonButton>
          </div>
        </div>
        <p className={styles.disclaimer}>** Sólo disponibles para solicitudes de tipo ecommerce</p>
      </div>

      <button
        type="button"
        onClick={handleContinue}
        className={styles.continueButton}
      >
        Confirmar
      </button>
    </div>
  );
}
